import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';

const DOC_TYPES = [
  { value: 'PROD_ORDER', label: 'Production Orders' },
  { value: 'GOODS_ISSUE', label: 'Goods Issue (Issue of Production)' },
  { value: 'GOODS_RECEIPT', label: 'Goods Receipt' },
  { value: 'INV_TRANSFER', label: 'Inventory Transfers' },
  { value: 'SALES_ORDER', label: 'Sales Orders' },
  { value: 'PURCHASE_ORDER', label: 'Purchase Orders' }
];

const GROUPS = [1, 2, 3];
const BRANCHES = ['Arm Welders Pvt.Ltd.', 'Arm Welders Ltd.', 'Branch Pune'];

export default function NumberingSettings() {
  const navigate = useNavigate();
  const [docType, setDocType] = useState('PROD_ORDER');
  const [seriesList, setSeriesList] = useState([]);
  const [filteredPeriod, setFilteredPeriod] = useState('All Periods');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [periods, setPeriods] = useState(['2022-23', '2023-24', '2024-25', '2025-26', '2026-27']);
  const [showNewPeriodModal, setShowNewPeriodModal] = useState(false);
  const [newPeriodValue, setNewPeriodValue] = useState('');
  const [seedNewPeriod, setSeedNewPeriod] = useState(true);
  // Why a next number jumped: the SAP document that took it, and when.
  const [numberLog, setNumberLog] = useState([]);
  const [showNumberLog, setShowNumberLog] = useState(false);

  // Window states (minimize / maximize / close)
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);

  // Load series on mount/change of docType
  useEffect(() => {
    loadSeries();
  }, [docType]);

  const loadNumberLog = async (type) => {
    try {
      setNumberLog(await api.get(`/settings/series/number-log?doc_type=${type}&limit=50`) || []);
    } catch {
      setNumberLog([]);
    }
  };

  const loadSeries = async () => {
    try {
      setLoading(true);
      const data = await api.get(`/settings/series?doc_type=${docType}`);
      setSeriesList(data || []);
      loadNumberLog(docType);
      setSelectedRowIndex(null);
      const dbPeriods = (data || []).map(s => s.period_indicator).filter(Boolean);
      setPeriods(prev => Array.from(new Set([...prev, ...dbPeriods])).sort());
    } catch (err) {
      showFlash(err.message || 'Failed to load document series', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showFlash = (msg, type = 'success') => {
    setFlash({ msg, type });
    setTimeout(() => setFlash(null), 5000);
  };

  const handleRowChange = (index, field, value) => {
    setSeriesList(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleAddRow = () => {
    const defaultPeriod = periods[periods.length - 1] || '2026-27'; // Default to the latest period
    let defaultPrefix = 'GST';
    if (docType === 'INV_TRANSFER') defaultPrefix = 'TRAN';
    else if (docType === 'SALES_ORDER') defaultPrefix = 'SO';

    // Auto calculate starting number based on latest numbers or period
    const currentYearCode = defaultPeriod.replace('20', '').replace('-', '');
    const firstNo = parseInt(`${currentYearCode}00001`, 10) || 262700001;

    const newRow = {
      series_id: null,
      series_name: `${defaultPrefix} ${defaultPeriod.replace('20', '')}`,
      doc_type: docType,
      prefix: defaultPrefix,
      suffix: '',
      remarks: '',
      group_id: 1,
      first_no: firstNo,
      next_no: firstNo,
      last_no: firstNo + 29999,
      is_default: seriesList.length === 0 ? 1 : 0,
      is_active: 1,
      digital_series: 0,
      branch_name: BRANCHES[0],
      period_indicator: defaultPeriod
    };
    setSeriesList(prev => [...prev, newRow]);
    setSelectedRowIndex(seriesList.length); // Select newly added row
  };

  const handleDeleteSelected = () => {
    if (selectedRowIndex === null) {
      showFlash('Please click a row to select it first.', 'error');
      return;
    }
    const targetRow = displayedSeriesList[selectedRowIndex];
    if (!targetRow) return;

    setSeriesList(prev => prev.filter((_, i) => i !== targetRow.originalIndex));
    setSelectedRowIndex(null);
  };

  const handleCreatePeriod = () => {
    const val = newPeriodValue.trim();
    if (!/^\d{4}-\d{2}$/.test(val)) {
      alert('Please enter period indicator in YYYY-YY format (e.g. 2027-28).');
      return;
    }

    if (periods.includes(val)) {
      alert(`Period "${val}" already exists.`);
      return;
    }

    const updatedPeriods = Array.from(new Set([...periods, val])).sort();
    setPeriods(updatedPeriods);

    if (seedNewPeriod) {
      const sortedPeriods = [...periods].sort();
      const latestPeriod = sortedPeriods[sortedPeriods.length - 1];

      if (latestPeriod) {
        const latestSeries = seriesList.filter(s => s.period_indicator === latestPeriod);

        if (latestSeries.length > 0) {
          const oldYearCode = latestPeriod.replace('20', '').replace('-', '');
          const newYearCode = val.replace('20', '').replace('-', '');

          const seededRows = latestSeries.map(s => {
            const replaceNum = (numVal) => {
              if (numVal === null || numVal === undefined) return numVal;
              const strVal = String(numVal);
              if (strVal.startsWith(oldYearCode)) {
                const replaced = strVal.replace(oldYearCode, newYearCode);
                return parseInt(replaced, 10);
              }
              return numVal;
            };

            const oldYearShort = latestPeriod.replace('20', '');
            const newYearShort = val.replace('20', '');
            let newName = s.series_name;
            if (newName.includes(oldYearShort)) {
              newName = newName.replace(oldYearShort, newYearShort);
            } else {
              newName = `${newName} ${newYearShort}`;
            }

            return {
              series_id: null,
              series_name: newName,
              doc_type: s.doc_type,
              prefix: s.prefix,
              suffix: s.suffix,
              remarks: `Seeded from ${latestPeriod}`,
              group_id: s.group_id,
              first_no: replaceNum(s.first_no) || parseInt(`${newYearCode}00001`, 10),
              next_no: replaceNum(s.first_no) || parseInt(`${newYearCode}00001`, 10),
              last_no: replaceNum(s.last_no) || (parseInt(`${newYearCode}00001`, 10) + 29999),
              is_default: s.is_default,
              is_active: 1,
              digital_series: s.digital_series,
              branch_name: s.branch_name,
              period_indicator: val
            };
          });

          setSeriesList(prev => [...prev, ...seededRows]);
          showFlash(`Seeded ${seededRows.length} series templates for period ${val}. Click OK to save.`);
        }
      }
    } else {
      showFlash(`Created period indicator ${val}. Click Add Row to configure new series.`);
    }

    setShowNewPeriodModal(false);
    setNewPeriodValue('');
  };

  const handleSetDefault = (originalIndex) => {
    setSeriesList(prev =>
      prev.map((row, i) => ({
        ...row,
        is_default: i === originalIndex ? 1 : 0
      }))
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Basic validations
      for (const row of seriesList) {
        if (!row.series_name || !String(row.series_name).trim()) {
          throw new Error('All series must have a Name.');
        }
        if (row.first_no === null || row.first_no === undefined || isNaN(row.first_no)) {
          throw new Error(`Series "${row.series_name}" has an invalid First No.`);
        }
        if (row.next_no === null || row.next_no === undefined || isNaN(row.next_no)) {
          throw new Error(`Series "${row.series_name}" has an invalid Next No.`);
        }
      }

      await api.put('/settings/series', {
        doc_type: docType,
        series: seriesList
      });
      showFlash('Document series settings saved successfully!');
      loadSeries();
    } catch (err) {
      showFlash(err.message || 'Failed to save series settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Get dynamic unique periods indicator list from loaded series for bottom filter
  const uniquePeriodIndicators = Array.from(
    new Set([...periods, ...seriesList.map(s => s.period_indicator).filter(Boolean)])
  ).sort().reverse();

  // Filter series list based on bottom indicator dropdown
  const displayedSeriesList = seriesList.map((row, originalIndex) => ({
    ...row,
    originalIndex
  })).filter(row => {
    if (filteredPeriod === 'All Periods') return true;
    return row.period_indicator === filteredPeriod;
  });

  const getDocTypeWindowTitle = () => {
    switch (docType) {
      case 'PROD_ORDER': return 'Production Orders';
      case 'GOODS_ISSUE': return 'Goods Issue';
      case 'GOODS_RECEIPT': return 'Goods Receipt';
      case 'INV_TRANSFER': return 'Inventory Transfers';
      case 'SALES_ORDER': return 'Sales Orders';
      case 'PURCHASE_ORDER': return 'Purchase Orders';
      default: return docType;
    }
  };

  const hasDigitalSeries = docType === 'PURCHASE_ORDER' || docType === 'SALES_ORDER';

  return (
    <div className={`p-6 mx-auto font-sans transition-all duration-200 ${isMaximized ? 'max-w-none w-full' : 'max-w-7xl'}`}>

      {/* Top selection bar */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-3">
          <span className="text-sm font-bold text-gray-700 dark:text-slate-300">Select Document Type:</span>
          <select
            value={docType}
            onChange={(e) => {
              setDocType(e.target.value);
              setFilteredPeriod('All Periods');
            }}
            className="border border-sap-border dark:border-slate-700 rounded-xl px-3 py-2 text-sm bg-white dark:bg-slate-900 text-sap-blue dark:text-slate-100 font-semibold focus:outline-none cursor-pointer"
          >
            {DOC_TYPES.map(d => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </select>
        </div>

        {/* Global Save Flash Message */}
        {flash && (
          <div className={`px-4 py-2 rounded-lg text-xs font-semibold border ${flash.type === 'error'
              ? 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900/60'
              : 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950/40 dark:text-green-300 dark:border-green-900/60'
            }`}>
            {flash.msg}
          </div>
        )}
      </div>

      {/* Main SAP-like Setup Window Mockup */}
      <div className={`bg-white dark:bg-slate-900 border border-gray-400 dark:border-slate-700 rounded shadow-2xl overflow-hidden transition-all duration-200 ${isMaximized ? 'fixed inset-4 z-[9999] flex flex-col' : 'relative'
        }`}>

        {/* Title Bar (Classic Desktop Window Style with requested bg-sap-blue color) */}
        <div className="bg-sap-blue text-white px-3 py-1.5 flex justify-between items-center text-xs font-semibold select-none">
          <span>Series - {getDocTypeWindowTitle()} - Setup</span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setIsMinimized(!isMinimized)}
              title={isMinimized ? "Restore Window" : "Minimize Window"}
              className="hover:bg-white/20 px-1.5 py-0.5 rounded text-white transition-colors"
            >
              －
            </button>
            <button
              type="button"
              onClick={() => setIsMaximized(!isMaximized)}
              title={isMaximized ? "Restore Layout" : "Maximize Layout"}
              className="hover:bg-white/20 px-1.5 py-0.5 rounded text-white transition-colors"
            >
              {isMaximized ? "⬜" : "⬜"}
            </button>
            <button
              type="button"
              onClick={() => navigate('/')}
              title="Close Settings"
              className="hover:bg-red-600 px-1.5 py-0.5 rounded text-white transition-colors"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Content body (Hidden if minimized) */}
        {!isMinimized && (
          <>
            {/* Grid Setup Table Container */}
            <div className={`overflow-x-auto border-b border-gray-300 dark:border-slate-800 ${isMaximized ? 'flex-1 min-h-0' : 'min-h-[300px]'
              }`}>
              <table className="w-full table-fixed min-w-[1200px] border-collapse text-xs text-sap-text dark:text-slate-100">
                <thead>
                  {/* Double Header Row 1 */}
                  <tr className="bg-[#efefef] dark:bg-slate-800/80">
                    <th rowSpan={2} className="w-10 border border-gray-300 dark:border-slate-700 text-center font-normal py-1">#</th>
                    <th rowSpan={2} className="w-32 border border-gray-300 dark:border-slate-700 text-left px-2 font-normal py-1">Name</th>
                    <th colSpan={3} className="border border-gray-300 dark:border-slate-700 text-center font-normal py-0.5">Number</th>
                    <th colSpan={2} className="border border-gray-300 dark:border-slate-700 text-center font-normal py-0.5">String</th>
                    <th rowSpan={2} className="w-28 border border-gray-300 dark:border-slate-700 text-left px-2 font-normal py-1">Remarks</th>
                    <th rowSpan={2} className="w-16 border border-gray-300 dark:border-slate-700 text-left px-2 font-normal py-1">Group</th>
                    <th rowSpan={2} className="w-24 border border-gray-300 dark:border-slate-700 text-left px-2 font-normal py-1">Period Ind.</th>
                    <th rowSpan={2} className="w-14 border border-gray-300 dark:border-slate-700 text-center font-normal py-1">Lock</th>
                    {hasDigitalSeries && (
                      <th rowSpan={2} className="w-20 border border-gray-300 dark:border-slate-700 text-center font-normal py-1">Digital Series</th>
                    )}
                    <th rowSpan={2} className="w-44 border border-gray-300 dark:border-slate-700 text-left px-2 font-normal py-1">Branch</th>
                    <th rowSpan={2} className="w-14 border border-gray-300 dark:border-slate-700 text-center font-normal py-1">Default</th>
                  </tr>
                  {/* Double Header Row 2 */}
                  <tr className="bg-[#efefef] dark:bg-slate-800/80">
                    <th className="w-28 border border-gray-300 dark:border-slate-700 text-left px-2 font-normal py-0.5">First No.</th>
                    <th className="w-28 border border-gray-300 dark:border-slate-700 text-left px-2 font-normal py-0.5">Next No.</th>
                    <th className="w-28 border border-gray-300 dark:border-slate-700 text-left px-2 font-normal py-0.5">Last No.</th>
                    <th className="w-16 border border-gray-300 dark:border-slate-700 text-left px-2 font-normal py-0.5">Prefix</th>
                    <th className="w-16 border border-gray-300 dark:border-slate-700 text-left px-2 font-normal py-0.5">Suffix</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={hasDigitalSeries ? 14 : 13} className="py-24 text-center text-gray-500 dark:text-slate-400">
                        <div className="flex flex-col items-center justify-center gap-2">
                          <div className="animate-spin h-6 w-6 border-2 border-[#3B82F6] border-t-transparent rounded-full" />
                          <span>Loading Series Data...</span>
                        </div>
                      </td>
                    </tr>
                  ) : displayedSeriesList.length === 0 ? (
                    <tr>
                      <td colSpan={hasDigitalSeries ? 14 : 13} className="py-20 text-center text-gray-500 dark:text-slate-400">
                        No series found for the current configuration. Use Add Row below.
                      </td>
                    </tr>
                  ) : (
                    displayedSeriesList.map((row, idx) => {
                      const isSelected = selectedRowIndex === idx;
                      const rowClass = isSelected
                        ? "bg-[#cce3f6] dark:bg-[#1a3a5c] hover:bg-[#cce3f6] dark:hover:bg-[#1a3a5c] cursor-pointer"
                        : "even:bg-[#fbfbfb] dark:even:bg-slate-800/20 hover:bg-gray-100/50 dark:hover:bg-slate-800/40 cursor-pointer";

                      return (
                        <tr
                          key={row.series_id || `temp-${idx}`}
                          className={rowClass}
                          onClick={() => setSelectedRowIndex(idx)}
                        >
                          {/* # index column */}
                          <td className="border border-gray-300 dark:border-slate-700 text-center py-0.5 font-medium select-none bg-gray-50 dark:bg-slate-900/60 text-gray-600 dark:text-slate-400">
                            {idx + 1}
                          </td>

                          {/* Name */}
                          <td className="border border-gray-300 dark:border-slate-700 py-0 px-0.5">
                            <input
                              type="text"
                              value={row.series_name || ''}
                              onChange={(e) => handleRowChange(row.originalIndex, 'series_name', e.target.value)}
                              className="w-full h-full bg-transparent border-none outline-none focus:bg-white dark:focus:bg-slate-800 px-1.5 py-1 text-xs"
                            />
                          </td>

                          {/* First No. */}
                          <td className="border border-gray-300 dark:border-slate-700 py-0 px-0.5">
                            <input
                              type="text"
                              value={row.first_no}
                              onChange={(e) => {
                                const val = e.target.value.replace(/\D/g, '');
                                handleRowChange(row.originalIndex, 'first_no', val === '' ? 0 : parseInt(val, 10));
                              }}
                              className="w-full h-full bg-transparent border-none outline-none focus:bg-white dark:focus:bg-slate-800 px-1.5 py-1 text-xs"
                            />
                          </td>

                          {/* Next No. */}
                          <td className="border border-gray-300 dark:border-slate-700 py-0 px-0.5">
                            <input
                              type="text"
                              value={row.next_no}
                              onChange={(e) => {
                                const val = e.target.value.replace(/\D/g, '');
                                handleRowChange(row.originalIndex, 'next_no', val === '' ? 0 : parseInt(val, 10));
                              }}
                              className="w-full h-full bg-transparent border-none outline-none focus:bg-white dark:focus:bg-slate-800 px-1.5 py-1 text-xs font-semibold text-blue-700 dark:text-sky-400"
                            />
                          </td>

                          {/* Last No. */}
                          <td className="border border-gray-300 dark:border-slate-700 py-0 px-0.5">
                            <input
                              type="text"
                              value={row.last_no ?? ''}
                              onChange={(e) => {
                                const val = e.target.value.trim() === '' ? null : parseInt(e.target.value, 10) || null;
                                handleRowChange(row.originalIndex, 'last_no', val);
                              }}
                              placeholder="Unlimited"
                              className="w-full h-full bg-transparent border-none outline-none focus:bg-white dark:focus:bg-slate-800 px-1.5 py-1 text-xs"
                            />
                          </td>

                          {/* Prefix */}
                          <td className="border border-gray-300 dark:border-slate-700 py-0 px-0.5">
                            <input
                              type="text"
                              value={row.prefix || ''}
                              onChange={(e) => handleRowChange(row.originalIndex, 'prefix', e.target.value)}
                              className="w-full h-full bg-transparent border-none outline-none focus:bg-white dark:focus:bg-slate-800 px-1.5 py-1 text-xs"
                            />
                          </td>

                          {/* Suffix */}
                          <td className="border border-gray-300 dark:border-slate-700 py-0 px-0.5">
                            <input
                              type="text"
                              value={row.suffix || ''}
                              onChange={(e) => handleRowChange(row.originalIndex, 'suffix', e.target.value)}
                              className="w-full h-full bg-transparent border-none outline-none focus:bg-white dark:focus:bg-slate-800 px-1.5 py-1 text-xs"
                            />
                          </td>

                          {/* Remarks */}
                          <td className="border border-gray-300 dark:border-slate-700 py-0 px-0.5">
                            <input
                              type="text"
                              value={row.remarks || ''}
                              onChange={(e) => handleRowChange(row.originalIndex, 'remarks', e.target.value)}
                              className="w-full h-full bg-transparent border-none outline-none focus:bg-white dark:focus:bg-slate-800 px-1.5 py-1 text-xs"
                            />
                          </td>

                          {/* Group */}
                          <td className="border border-gray-300 dark:border-slate-700 py-0 px-0.5">
                            <select
                              value={row.group_id}
                              onChange={(e) => handleRowChange(row.originalIndex, 'group_id', parseInt(e.target.value, 10))}
                              className="w-full h-full bg-transparent border-none outline-none focus:bg-white dark:focus:bg-slate-800 px-1.5 py-1 text-xs select-none cursor-pointer"
                            >
                              {GROUPS.map(g => (
                                <option key={g} value={g} className="bg-white dark:bg-slate-900">{g}</option>
                              ))}
                            </select>
                          </td>

                          {/* Period Ind. */}
                          <td className="border border-gray-300 dark:border-slate-700 py-0 px-0.5">
                            <select
                              value={row.period_indicator}
                              onChange={(e) => handleRowChange(row.originalIndex, 'period_indicator', e.target.value)}
                              className="w-full h-full bg-transparent border-none outline-none focus:bg-white dark:focus:bg-slate-800 px-1.5 py-1 text-xs cursor-pointer"
                            >
                              {periods.map(p => (
                                <option key={p} value={p} className="bg-white dark:bg-slate-900">{p}</option>
                              ))}
                            </select>
                          </td>

                          {/* Lock */}
                          <td className="border border-gray-300 dark:border-slate-700 py-1 text-center align-middle">
                            <input
                              type="checkbox"
                              checked={row.is_active === 0}
                              onChange={(e) => handleRowChange(row.originalIndex, 'is_active', e.target.checked ? 0 : 1)}
                              className="h-3.5 w-3.5 rounded border-gray-300 dark:border-slate-700 text-sap-blue focus:ring-sap-blue cursor-pointer"
                            />
                          </td>

                          {/* Digital Series (for PO / SO only) */}
                          {hasDigitalSeries && (
                            <td className="border border-gray-300 dark:border-slate-700 py-1 text-center align-middle">
                              <input
                                type="checkbox"
                                checked={row.digital_series === 1}
                                onChange={(e) => handleRowChange(row.originalIndex, 'digital_series', e.target.checked ? 1 : 0)}
                                className="h-3.5 w-3.5 rounded border-gray-300 dark:border-slate-700 text-sap-blue focus:ring-sap-blue cursor-pointer"
                              />
                            </td>
                          )}

                          {/* Branch */}
                          <td className="border border-gray-300 dark:border-slate-700 py-0 px-0.5">
                            <select
                              value={row.branch_name}
                              onChange={(e) => handleRowChange(row.originalIndex, 'branch_name', e.target.value)}
                              className="w-full h-full bg-transparent border-none outline-none focus:bg-white dark:focus:bg-slate-800 px-1.5 py-1 text-xs cursor-pointer"
                            >
                              {BRANCHES.map(b => (
                                <option key={b} value={b} className="bg-white dark:bg-slate-900">{b}</option>
                              ))}
                            </select>
                          </td>

                          {/* Default selection */}
                          <td className="border border-gray-300 dark:border-slate-700 py-1 text-center align-middle">
                            <input
                              type="radio"
                              name={`sap_default_${docType}`}
                              checked={row.is_default === 1}
                              onChange={() => handleSetDefault(row.originalIndex)}
                              className="h-3.5 w-3.5 text-sap-blue focus:ring-sap-blue cursor-pointer"
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Dynamic Filter Section */}
            <div className="bg-[#f0f0f0] dark:bg-slate-800/40 px-3 py-2 flex items-center border-b border-gray-300 dark:border-slate-800">
              <div className="flex items-center gap-2">
                <span className="text-xs text-sap-textLight">Display Series Linked to Selected Period(s) Indicator</span>
                <select
                  value={filteredPeriod}
                  onChange={(e) => {
                    if (e.target.value === 'DEFINE_NEW') {
                      setShowNewPeriodModal(true);
                      setFilteredPeriod('All Periods');
                    } else {
                      setFilteredPeriod(e.target.value);
                    }
                  }}
                  className="border border-gray-300 dark:border-slate-700 rounded-sm px-2 py-0.5 text-xs bg-white dark:bg-slate-800 text-sap-text dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-sap-blue cursor-pointer font-medium"
                >
                  <option value="All Periods">All Periods</option>
                  {uniquePeriodIndicators.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                  <option value="DEFINE_NEW" className="text-blue-600 dark:text-sky-400 font-semibold">Add Financial Year</option>
                </select>
              </div>
            </div>

            {/* Footer Actions Panel */}
            <div className="bg-[#f4f4f4] dark:bg-slate-800/60 px-4 py-3 flex justify-between items-center">

              {/* Main Action Buttons (OK / Cancel) */}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving || loading}
                  className="px-6 py-1 bg-[#f0ab15] hover:bg-[#d89912] active:bg-[#c08710] border border-gray-400 text-black text-xs font-semibold rounded shadow-sm flex items-center gap-1.5 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'OK'}
                </button>
                <button
                  type="button"
                  onClick={loadSeries}
                  className="px-6 py-1 bg-[#e1e1e1] hover:bg-[#d0d0d0] active:bg-[#c0c0c0] border border-gray-400 text-black text-xs font-semibold rounded shadow-sm transition-colors"
                >
                  Cancel
                </button>
              </div>

              {/* Row editing helpers */}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleAddRow}
                  className="px-3 py-1 bg-white hover:bg-gray-100 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-white border border-gray-400 dark:border-slate-600 text-xs rounded transition-colors"
                >
                  ➕ Add Row
                </button>
                <button
                  type="button"
                  onClick={handleDeleteSelected}
                  disabled={selectedRowIndex === null}
                  className="px-3 py-1 bg-white hover:bg-gray-100 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-white border border-gray-400 dark:border-slate-600 text-xs rounded transition-colors disabled:opacity-40"
                >
                  🗑️ Delete Row
                </button>
              </div>

            </div>
          </>
        )}

      </div>

      {showNewPeriodModal && (
        <div className="fixed inset-0 z-[100080] flex items-center justify-center bg-black/40">
          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-sap-border w-[400px] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-150">

            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2.5 bg-sap-blue">
              <span className="text-white text-xs font-semibold">Define New Period (Financial Year)</span>
              <button
                type="button"
                onClick={() => { setShowNewPeriodModal(false); setNewPeriodValue(''); }}
                className="text-white hover:opacity-80 text-base leading-none"
              >
                ✕
              </button>
            </div>

            {/* Body */}
            <div className="p-4 space-y-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-sap-textLight dark:text-slate-300">Period Indicator (e.g. 2027-28)</label>
                <input
                  type="text"
                  autoFocus
                  placeholder="YYYY-YY"
                  value={newPeriodValue}
                  onChange={(e) => setNewPeriodValue(e.target.value)}
                  className="w-full h-8 px-3 text-xs border border-sap-border rounded-lg focus:outline-none focus:border-sap-blue bg-white dark:bg-slate-800 dark:text-white"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="seedNewPeriod"
                  checked={seedNewPeriod}
                  onChange={(e) => setSeedNewPeriod(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 dark:border-slate-700 text-sap-blue focus:ring-sap-blue cursor-pointer"
                />
                <label htmlFor="seedNewPeriod" className="text-xs text-sap-text dark:text-slate-300 cursor-pointer select-none">
                  Automatically duplicate current series templates
                </label>
              </div>
            </div>

            {/* Footer */}
            <div className="px-4 py-2 border-t border-sap-border bg-gray-50 dark:bg-slate-800/20 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => { setShowNewPeriodModal(false); setNewPeriodValue(''); }}
                className="px-3 py-1 text-xs border border-sap-border rounded hover:bg-gray-100 dark:hover:bg-slate-800 dark:text-white"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreatePeriod}
                disabled={!newPeriodValue.trim()}
                className="px-4 py-1 text-xs font-semibold bg-[#f0ab15] hover:bg-[#d89912] border border-gray-400 text-black rounded disabled:opacity-50"
              >
                Create
              </button>
            </div>

          </div>
        </div>
      )}

      {/* Automatic numbering changes — what moved, why, and when */}
      <div className="mt-4 rounded-xl border border-sap-border dark:border-slate-700 bg-white dark:bg-slate-900">
        <button
          type="button"
          onClick={() => setShowNumberLog((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-2.5 text-left"
        >
          <span className="text-sm font-semibold text-gray-700 dark:text-slate-200">
            Automatic numbering changes
            {numberLog.length > 0 && (
              <span className="ml-2 text-xs font-normal text-gray-500 dark:text-slate-400">
                {numberLog.length} for {DOC_TYPES.find((d) => d.value === docType)?.label || docType}
              </span>
            )}
          </span>
          <span className="text-xs text-sap-blue">{showNumberLog ? 'Hide' : 'Show'}</span>
        </button>

        {showNumberLog && (
          <div className="border-t border-sap-border dark:border-slate-700 px-4 py-3">
            <p className="text-xs text-gray-500 dark:text-slate-400 mb-2">
              Numbers used in SAP cannot be used again here, so the series moves past them
              on its own — within seconds of SAP creating the document, and once an hour as
              a check. Nothing here changes a number you set by hand to something higher.
            </p>
            {numberLog.length === 0 ? (
              <p className="text-xs text-gray-500 dark:text-slate-400 py-2">
                No automatic changes recorded for this document type yet.
              </p>
            ) : (
              <div className="overflow-x-auto max-h-80">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 dark:bg-slate-800/40 text-gray-500 dark:text-slate-400 sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1.5 font-semibold">When</th>
                      <th className="text-left px-2 py-1.5 font-semibold">Series</th>
                      <th className="text-left px-2 py-1.5 font-semibold">Used in SAP</th>
                      <th className="text-left px-2 py-1.5 font-semibold">Next number changed</th>
                      <th className="text-left px-2 py-1.5 font-semibold">Noticed by</th>
                    </tr>
                  </thead>
                  <tbody>
                    {numberLog.map((r) => (
                      <tr key={r.log_id} className="border-t border-sap-border/50 dark:border-slate-700/60">
                        <td className="px-2 py-1.5 whitespace-nowrap text-gray-600 dark:text-slate-300">
                          {new Date(r.created_at).toLocaleString('en-IN', {
                            day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true,
                          })}
                        </td>
                        <td className="px-2 py-1.5 font-medium text-gray-700 dark:text-slate-200">{r.series_name || '—'}</td>
                        <td className="px-2 py-1.5 font-mono text-gray-700 dark:text-slate-200">
                          {r.sap_doc_no ?? '—'}
                          {r.business_object ? (
                            <span className="ml-1 font-sans text-gray-400">({r.business_object})</span>
                          ) : null}
                        </td>
                        <td className="px-2 py-1.5 font-mono">
                          <span className="text-gray-400 line-through">{r.from_no}</span>
                          <span className="mx-1 text-gray-400">→</span>
                          <span className="font-semibold text-sap-blue">{r.to_no}</span>
                        </td>
                        <td className="px-2 py-1.5 text-gray-600 dark:text-slate-300">
                          {r.source === 'webhook' ? 'SAP notification' : r.source === 'hourly' ? 'Hourly check' : 'Manual'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

    </div>
  );
}
